import Flickity from "flickity-fade";

/**
 * SearchMap
 */
export default class SearchMap {
    markers = [];
    progress = 0;
    selectedIndex = 0;
    slider = null;
    type = "";

    constructor(mapboxKey) {
        mapboxgl.accessToken = mapboxKey;
    }

    lineJson = {
        type: "FeatureCollection",
        features: [
            {
                type: "Feature",
                geometry: {
                    type: "LineString",
                    coordinates: [],
                },
            },
        ],
    };

    init() {
        this.initMap();

        window.addEventListener("resizemap", () => {
            this.mapResize();
        });
        window.addEventListener("update-map-type", (event) => {
            this.type = event.detail.type;
            this.initMap();
        });
        window.addEventListener(
            "update-map-locations",
            (event) => {
                this.type = event.detail.type;
                this.updateMapLocations(
                    event.detail.locations,
                    event.detail.type,
                    event.detail.setBounds,
                    event.detail.zoomToLevel ?? 0,
                    event.detail.disablePopup ?? false
                );
            },
            false
        );

        window.addEventListener("highlightmapmarker", (e) => {
            const coordinates = e.detail.coordinates;
            const markerButtons = document.querySelectorAll(".marker");
            markerButtons.forEach((marker) => {
                marker.classList.remove("-highlighted");
            });

            const marker = document.querySelector(
                `[data-pin-coordinates="${coordinates}"]`
            );
            if (marker) {
                marker.classList.add("-highlighted");
            }
        });
    }
    initMap() {
        const mapSettings = {
            container: "search-map-box",
            style: "mapbox://styles/james-pnp/cjui3pknm2kn81ftitqboac27",
            interactive: true,
            scrollZoom: true,
            dragRotate: false,
            boxZoom: false,
            dragPan: true,
            keyboard: {
                enabled: true,
            },
            cooperativeGestures: false,
            attributionControl: false,
            zoom: 3,
            maxZoom: 18,
        };

        this.map = new mapboxgl.Map(mapSettings);

        this.map.addControl(
            new mapboxgl.NavigationControl({
                showCompass: false,
            }),
            "bottom-left"
        );

        this.map.on("load", () => {
            if (this.type !== "trip") {
                return;
            }

            if (this.map.getSource("LineString") !== undefined) {
                return;
            }

            this.map.addSource("LineString", {
                type: "geojson",
                data: [],
            });

            this.map.addLayer({
                id: "LineString",
                type: "line",
                source: "LineString",
                layout: {
                    "line-join": "round",
                    "line-cap": "round",
                },
                paint: {
                    "line-color": "#000000",
                    "line-width": 1.6,
                    "line-opacity": 0.3,
                },
            });

            this.lineJson = this.map.getSource("LineString");
        });

        this.mapResize();
    }

    async clearMarkers() {
        for (let i = 0; i < this.markers.length; i++) {
            this.markers[i].marker.remove();
        }
        // Clearing the ghost markers comes from map box storage in certain situations
        document
            .querySelectorAll(".mapboxgl-marker")
            .forEach((marker) => marker.remove());
    }

    async updateMapLocations(
        locations,
        type,
        setBounds = true,
        zoomToLevel = 0,
        disablePopup = false
    ) {
        await this.clearMarkers();

        if (!locations?.length) return;

        if (setBounds) {
            let bounds = locations.reduce(function (bounds, location) {
                return bounds.extend(location.coordinates);
            }, new mapboxgl.LngLatBounds(
                locations[0].coordinates,
                locations[0].coordinates
            ));

            for (const location of locations) {
                bounds.extend(location.coordinates);
            }

            this.map.fitBounds(bounds, {
                padding: this.getPadding(),
            });
        }

        let markers = [];

        locations.forEach((location) => {
            const locationType = location?.type ?? type;
            let popup = null;

            if (!location.isAirport) {
                popup = new mapboxgl.Popup({
                    offset: 25,
                }).setHTML(`
                <div class="SearchMapPopup w-[393px]">
                    <div class="relative aspect-[393/250] flex items-end p-5"></div>
                    <div class="flex flex-col gap-[30px] p-[30px]">
                        <div>
                            <div class="inline-flex items-center gap-x-[10px] button-solid-primary py-3 px-6 rounded text-[15px] transition duration-300">
                                ${
                                    locationType === "experience"
                                        ? "Read more"
                                        : ""
                                }
                                ${
                                    locationType === "location"
                                        ? "Go to location"
                                        : ""
                                }
                                ${locationType === "lodge" ? "Go to lodge" : ""}
                                ${locationType === "trip" ? "Go to trip" : ""}

                                <svg width="16" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11.8537 5.85403L7.35375 10.354C7.25993 10.4478 7.13268 10.5006 7 10.5006C6.86732 10.5006 6.74007 10.4478 6.64625 10.354C6.55243 10.2602 6.49972 10.133 6.49972 10.0003C6.49972 9.8676 6.55243 9.74035 6.64625 9.64653L10.2931 6.00028H0.5C0.367392 6.00028 0.240215 5.9476 0.146447 5.85383C0.0526785 5.76006 0 5.63289 0 5.50028C0 5.36767 0.0526785 5.24049 0.146447 5.14672C0.240215 5.05296 0.367392 5.00028 0.5 5.00028H10.2931L6.64625 1.35403C6.55243 1.26021 6.49972 1.13296 6.49972 1.00028C6.49972 0.867596 6.55243 0.740348 6.64625 0.646528C6.74007 0.552708 6.86732 0.5 7 0.5C7.13268 0.5 7.25993 0.552708 7.35375 0.646528L11.8537 5.14653C11.9002 5.19296 11.9371 5.24811 11.9623 5.30881C11.9874 5.36951 12.0004 5.43457 12.0004 5.50028C12.0004 5.56599 11.9874 5.63105 11.9623 5.69175C11.9371 5.75245 11.9002 5.80759 11.8537 5.85403Z" fill="currentColor"></path>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            `);

                popup.on("open", () => {
                    this.progress = 0;
                    const urlSearchParams = new URLSearchParams({
                        action: "wp_ajax_nopriv_get_location_map_card",
                        id: location.id,
                        type: locationType,
                    });

                    pegasus
                        .fetchContent(
                            ajaxurl + "?" + urlSearchParams.toString()
                        )
                        .then((resp) => {
                            if (!resp.ok) {
                                throw Error(resp.statusText);
                            }
                            return resp.text();
                        })
                        .then((text) => {
                            popup.setHTML(text);

                            const searchMapPopup = popup
                                .getElement()
                                .querySelector(".SearchMapPopup");
                            const images = popup
                                .getElement()
                                .querySelector(".SearchMapPopup__Carousel");
                            const infoButton = popup
                                .getElement()
                                .querySelector(".SearchMapPopup__InfoButton");

                            const slides =
                                this.$root.querySelectorAll(".flickity-cell");

                            if (images) {
                                this.slider = new Flickity(images, {
                                    fade: true,
                                    on: {
                                        change: () => {
                                            this.selectedIndex =
                                                this.slider.selectedIndex;
                                            this.progress =
                                                ((this.selectedIndex + 1) /
                                                    slides.length) *
                                                100;
                                        },
                                    },
                                });
                            }

                            if (infoButton) {
                                infoButton.addEventListener("click", () => {
                                    if (
                                        searchMapPopup.classList.contains(
                                            "SearchMapPopup--ShowDetails"
                                        )
                                    ) {
                                        searchMapPopup.classList.remove(
                                            "SearchMapPopup--ShowDetails"
                                        );
                                    } else {
                                        searchMapPopup.classList.add(
                                            "SearchMapPopup--ShowDetails"
                                        );
                                    }
                                });
                            }
                        });
                });
            }

            const stm = document.createElement("button");
            stm.className =
                "marker -marker-highlighted in-search-results mapboxgl-marker";
            stm.dataset.pinCoordinates = location.coordinates;

            let marker = null;
            if (location.isAirport) {
                marker = new mapboxgl.Marker(stm)
                    .setLngLat(location.coordinates)
                    .addTo(this.map);
            } else {
                marker = new mapboxgl.Marker(stm)
                    .setLngLat(location.coordinates)
                    .setPopup(popup)
                    .addTo(this.map);
            }

            markers.push({
                id: location?.id ? parseInt(location.id) : null,
                marker,
            });

            const getLngAdjust = (zoomLevel) => {
                if (zoomLevel >= 6) return 2;
                if (zoomLevel === 5) return 4;
                if (zoomLevel === 4) return 7.5;
                if (zoomLevel === 2) return 35;
                return 0;
            };

            // Add click event listener to each marker
            stm.addEventListener("click", () => {
                marker.togglePopup();
            });

            // Handle auto-popup when opening the map
            if (location.openPopup) {
                if (!disablePopup) {
                    marker.togglePopup();
                }

                this.map.flyTo({
                    center: [location.coordinates[0], location.coordinates[1]],
                    zoom: parseInt(zoomToLevel) || 5,
                    speed: 1.5,
                    curve: 1,
                });
            }
        });

        if (this.type === "trip") {
            let lineCoordinates = locations.map(
                (location) => location.coordinates
            );
            this.lineJson.setData({
                type: "Feature",
                geometry: {
                    type: "LineString",
                    coordinates: lineCoordinates,
                },
            });
        }

        this.markers = markers;
    }

    mapResize() {
        const resize = setInterval(() => {
            this.map.resize();
        }, 10);
        setTimeout(() => {
            clearInterval(resize);
        }, 1000);
    }

    getVpWidth() {
        return window.innerWidth > 0 ? window.innerWidth : screen.width;
    }

    getPadding() {
        // adjust mapbox padding depending on the viewport width
        return this.getVpWidth() < 1300 ? 160 : 200;
    }
}

document.addEventListener("alpine:init", () => {
    Alpine.data("searchMap", (mapboxKey) => new SearchMap(mapboxKey));
});
